import { FlexContainer } from '@/styles/Containers';
import { GreyText, Letter } from '@/styles/Text';
import React, { useMemo } from 'react';
import { useState } from 'react';
import styled from 'styled-components';
import AddCourseModal from './AddCourseModal';
import axios from 'axios';
import API_URL from '@/config';
import { useQuery } from '@tanstack/react-query';
import { CourseType } from '@/models/course';
import buildQueryString from '@/utils/buildQueryString';
import { usePaginationContext } from '@/context/PaginationContext';
import { CalculatorCourseProps } from './CalculatorTypes';

export const DropdownContainer = styled.div`
  position: sticky;
  align-self: center;
  height: auto;
  margin-top: -2.5rem;
  margin-left: 0.5rem;
  max-height: 9rem;
  width: 18rem;
  border-bottom-left-radius: 1.9rem;
  border-bottom-right-radius: 1.9rem;
  color: ${({ theme }) => theme.color.black};
  border: 0.5px solid ${({ theme }) => theme.color.black};
  background-color: ${({ theme }) => theme.color.white};
  text-align: left;
  overflow-y: auto;
  @media ${({ theme }) => theme.device.tablet} {
    margin-left: 0rem;
  }
`;

const AddCourseContainer = styled.button`
  position: sticky;
  height: 2.4rem;
  width: 18rem;
  border-top: 0.5px solid;
  border-bottom: none;
  border-left: none;
  border-right: none;
  border-bottom-left-radius: 1.88rem;
  border-bottom-right-radius: 1.88rem;
  bottom: 0;
  background-color: ${({ theme }) => theme.color.white};
  :hover {
    background-color: ${({ theme }) => theme.color.darkBlue};
  }
  :hover > * {
    color: ${({ theme }) => theme.color.white};
  }
  z-index: 10;
`;

const CourseItem = styled.div<{ isHovered: boolean; isLast: boolean }>`
  border-bottom: ${({ isLast }) => (isLast ? 'none' : '0.5px solid black')};
  transition: background-color 0.1s ease;
  background-color: ${({ isHovered }) => (isHovered ? '#1E3859' : 'white')};
  cursor: pointer;
`;

const CourseCode = styled(Letter)<{ isHovered: boolean }>`
  margin: 0.5rem 1rem;
  font-size: 13px;
  color: ${({ isHovered }) => (isHovered ? 'white' : 'black')};
  padding: 0 0.5rem;
`;

const CourseName = styled(Letter)<{ isHovered: boolean }>`
  left: 6rem;
  font-size: 0.8rem;
  color: ${({ isHovered }) => (isHovered ? 'white' : 'black')};
  flex: 1;
  text-align: center;
`;

interface DropDownCoursesProps {
  inputValue: string;
  addCourseToSemester: (course: CalculatorCourseProps) => void;
}

interface CourseListType {
  count: number;
  data: CourseType[];
}
type DropDownCourse = {
  courseCode: string;
  courseName: string;
  coursePoint: string;
};

const DropDownCourses = ({ inputValue, addCourseToSemester }: DropDownCoursesProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [courses, setCourses] = useState<CourseType[]>([]);
  const { coursePagination, setCoursePagination } = usePaginationContext();
  const resultLimit = 5; //funker ikke i backenden?
  const [hoveredCourse, setHoveredCourse] = useState('');
  const [originalCourses, setOriginalCourses] = useState<CourseType[]>([]);
  const [courseSelected, setCourseSelected] = useState<DropDownCourse>();

  const searchOptions = useMemo(
    () => ({
      n: resultLimit,
      search: inputValue,
      offset: (coursePagination.currentPage - 1) * resultLimit,
    }),
    [inputValue, coursePagination.currentPage],
  );

  useQuery<CourseType[]>(['course-list', searchOptions], async () => {
    const response = await axios.get(`${API_URL}/course/all/${buildQueryString({ ...searchOptions })}`);
    const { data, count } = response.data as CourseListType;
    const courses = data;
    const originalCourses = courses;
    const updatedCourses = courses.map((course) => {
      if (course.course_name_norwegian.length > 11) {
        return {
          ...course,
          course_name_norwegian: course.course_name_norwegian.substring(0, 9) + '...',
        };
      }
      return course;
    });

    setCoursePagination((pagination) => ({ ...pagination, totalPages: Math.ceil(count / resultLimit) }));
    setCourses(updatedCourses);
    setOriginalCourses(originalCourses);
    return data;
  });

  const handleCourseClick = (courseCodeInput: string) => {
    const selectedCourse = originalCourses.find((course) => course.course_code === courseCodeInput);
    if (selectedCourse) {
      setCourseSelected({
        courseCode: selectedCourse.course_code,
        courseName: selectedCourse.course_name_norwegian,
        coursePoint: selectedCourse.credit.toString(),
      });
      const newCourse: CalculatorCourseProps = {
        courseCode: selectedCourse.course_code,
        courseName: selectedCourse.course_name_norwegian,
        coursePoint: selectedCourse.credit,
        grade: -2,
      };
      addCourseToSemester(newCourse);
    }
  };

  return (
    <DropdownContainer>
      {courses.map((course, index) => (
        <CourseItem
          key={`${course.course_code}-${index}`}
          isHovered={hoveredCourse === course.course_code}
          isLast={index === courses.length - 1}
          onMouseEnter={() => setHoveredCourse(course.course_code)}
          onMouseLeave={() => setHoveredCourse('')}
          onClick={() => handleCourseClick(course.course_code)}
        >
          <FlexContainer flexDirection='row' justifyContent='space-between' alignItems='center'>
            <CourseCode isHovered={hoveredCourse === course.course_code}>{course.course_code}</CourseCode>
            <CourseName isHovered={hoveredCourse === course.course_code}>{course.course_name_norwegian}</CourseName>
            <GreyText textAlign='right' margin='0.5rem 1rem 0 0 '>
              {course.credit} stp
            </GreyText>
          </FlexContainer>
        </CourseItem>
      ))}
      <AddCourseContainer>
        <Letter fontSize='0.8rem' textAlign='left' margin='0.5rem 0.8rem' fontWeight={600} onClick={() => setIsOpen(true)}>
          Legg til eget emne
        </Letter>
        <AddCourseModal isOpen={isOpen} handleCloseModal={setIsOpen} addCourseToSemester={addCourseToSemester} />
      </AddCourseContainer>
    </DropdownContainer>
  );
};

export default React.memo(DropDownCourses);
